import { applyMiddleware, createStore, Store, Middleware } from 'redux'
import { State } from 'interfaces/state'
import { createEpicMiddleware } from 'redux-observable'
import { get, post, patch, deleteAjax, sendBeacon } from 'helpers/observableAjaxHelpers'
import epics, { onResetState } from 'epics'
import reducers from 'reducers'
import { EpicDependencies } from 'interfaces/middleware'
import { remote } from 'helpers/mobileAppHelpers'
import { defaultMemoize } from 'reselect'
import { composeWithDevTools } from '@redux-devtools/extension'
import { isFeatureEnabled } from 'helpers/featureHelpers'
import { consoleRelay } from 'components/features/webSocketLogger/ConsoleWSRelay'
import { BehaviorSubject, Observable } from 'rxjs'
import { switchMap } from 'rxjs/operators'

interface ImportWithDefault {
  'default': any
}

const getEpicMiddleware = defaultMemoize(() => createEpicMiddleware(
  {
    dependencies: {
      document,
      navigator,
      get,
      post,
      sendBeacon,
      patch,
      deleteAjax,
      remote,
    },
  },
))

type RootEpicRunner = (action$: unknown, store: Store<State>, dependencies: EpicDependencies) => Observable<unknown>

const getEpicsSubject = defaultMemoize(
  () => new BehaviorSubject(epics as unknown as RootEpicRunner)
)

const replaceRootEpic = (nextEpic: unknown) => {
  getEpicsSubject().next(nextEpic as RootEpicRunner)
}

const actionSanitizer = (action: any) => {
  if (action.type === 'AGORA_CLIENT_EVENT' || action.type === 'AGORA_STREAM_EVENT') {
    const { payload, ...restAction } = action
    const { stream, ...restPayload } = payload
    return {
      ...restAction,
      type: `${ action.type }:${ action.payload.type }`,
      payload: {
        ...restPayload,
        ...stream ? { stream: { id: stream.getId() }} : {},
      },
    }
  }
  if (action.type === 'AGORA_SCREEN_SHARE_STREAM') {
    const { payload, ...restAction } = action
    const { params, ...restPayload } = payload
    const { stream, ...restParams } = params

    return {
      ...restAction,
      payload: {
        ...restPayload,
        params: {
          ...restParams,
          ...stream ? { stream: { id: stream.getId() }} : {},
        },
      },
    }
  }
  if (action.meta && typeof action.meta === 'string') {
    return {
      ...action,
      type: `${ action.type }/${ action.meta }`,
    }
  }

  return action
}

const getComposeEnhancers = () => composeWithDevTools({
    actionSanitizer,
    serialize: {
      function: function (fn: () => void) { return fn.toString() },
    },
  })

const logActionsMiddleware: Middleware = () => (next) => (rawAction) => {
  const isActionsLogEnabled = isFeatureEnabled('actions')
  const isConsoleWSRelayEnabled = consoleRelay.isStarted

  const shouldLog = isActionsLogEnabled || isConsoleWSRelayEnabled || DEBUG_ACTIONS
  if (!shouldLog) {
    return next(rawAction)
  }

  const action = actionSanitizer(rawAction)

  if (isFeatureEnabled('actions') || isConsoleWSRelayEnabled || DEBUG_ACTIONS) {
    console.log('ACTION:', action.type, (action as any).meta || '', (action as any).payload?.type || '')
  }

  return next(rawAction)
}

// FOR DEBUG
const DEBUG_ACTIONS = false
const TRACED_ACTIONS: string[] = ['ACTIVATE_ANCHOR']
const traceActionsMiddleware: Middleware = () => {
  return (next) => (action) => {
    if (TRACED_ACTIONS.includes(action.type)) {
      console.trace(action.type)
    }

    return next(action)
  }
}

export const getStore = defaultMemoize((): Store<State> => {
  const epicMiddleware = getEpicMiddleware()
  const store = getComposeEnhancers()(
    DEBUG_ACTIONS
      ? applyMiddleware(
        logActionsMiddleware,
        epicMiddleware,
        traceActionsMiddleware
      )
      : applyMiddleware(
        logActionsMiddleware,
        epicMiddleware,
      )
  )(createStore)(reducers)

  ;(epicMiddleware as unknown as { run: (rootEpic: RootEpicRunner) => void })
    .run((action$, _state$, dependencies) => getEpicsSubject().pipe(
      switchMap((epic) => epic(action$, store, dependencies))
    ))

  onResetState(replaceRootEpic, store.dispatch)

  return store
})


if (module.hot) {
  // Enable Webpack hot module replacement for reducers
  module.hot.accept('reducers/index', () => {
    // tslint:disable-next-line:no-require-imports
    const nextReducers = (require('reducers') as ImportWithDefault).default

    getStore().replaceReducer(nextReducers)
  })

  module.hot.accept('epics/index', () => {
    // tslint:disable-next-line:no-require-imports
    const nextEpics = (require('epics') as ImportWithDefault).default

    replaceRootEpic(nextEpics)
  })
}

